import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, retry, take, timeout } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from 'environments/environment';
import { UserLogin } from 'app/models/UserLogin';
import { UserService } from 'app/services/user.service';
import { UtilityService } from 'app/services/utility.service';
import { readJwtClaims } from 'app/services/jwt-claims.util';

const AUTH_LOG_PREFIX = '[auth-debug]';
/** The in-app route, as the router knows it. */
const LOGIN_ROUTE = '/authentication/login';
/**
 * The browser address for that route. The app is bootstrapped with `useHash: true`, so every
 * in-app location lives after the '#' and a bare path would not reach the router.
 */
const LOGIN_URL = `/#${LOGIN_ROUTE}`;
/**
 * Hands the interrupted route across the forced-logout reload. It travels in sessionStorage, not
 * in the login URL's query string: the hash-only navigation to the login URL leaves the dying app
 * running until the reload commits, and in that window its guards rewrite the query string (the
 * default route's guard replaces it with returnUrl=/dashboard). Session-scoped so each tab returns
 * to its own page. The login component reads it without removing it, because the dying app can
 * activate the login component once before the reload commits and a destructive read there would
 * eat the value; every successful sign-in wipes it with the rest of session storage.
 */
export const POST_LOGOUT_RETURN_URL_KEY = 'postLogoutReturnUrl';
const REFRESH_RETRY_DELAY_MS = 500;
const REFRESH_TIMEOUT_MS = 15000;
const LOGOUT_API_TIMEOUT_MS = 2000;

/** Refresh below this much remaining validity rather than letting a request 401 first. */
const FRESHNESS_THRESHOLD_SECONDS = 60;
/** Assumed access-token lifetime when the token carries no usable iat/exp pair. */
const FALLBACK_TOKEN_LIFETIME_SECONDS = 3600;

/**
 * Owns silent token refresh: one in-flight refresh at a time, with everything else queued behind
 * it, plus the forced-logout transition when the session cannot be continued.
 *
 * Root-provided deliberately. ErrorInterceptor is registered in the root module and again in two
 * lazy feature modules, so those modules get their own interceptor instances; holding the
 * single-flight state here means all of them coordinate through one latch instead of each running
 * its own refresh.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
	private isRefreshing = false;
	private refreshTokenSubject = new BehaviorSubject<string>(null);
	private loggingOut = false;

	/** Bypasses the interceptor chain so a refresh can never recurse into itself. */
	private readonly http: HttpClient;

	constructor(
		private readonly userService: UserService,
		private readonly utilityService: UtilityService,
		httpBackend: HttpBackend
	) {
		this.http = new HttpClient(httpBackend);
	}

	/**
	 * Emits the new access token once a refresh completes. Callers that arrive while one is
	 * already running queue behind it rather than starting a second.
	 */
	refresh(): Observable<string> {
		if (this.isRefreshing) {
			return this.refreshTokenSubject.pipe(
				filter((token) => token !== null),
				take(1)
			);
		}

		const userId = this.userService.currentUserValue?.user?.id;
		const refreshToken = this.userService.currentUserValue?.refreshToken;

		if (!userId || !refreshToken) {
			console.warn(`${AUTH_LOG_PREFIX} no stored session to refresh — signing out`);
			this.forceLogout();
			return throwError(() => 'Your session has ended. Please log in again.');
		}

		this.isRefreshing = true;
		// An RxJS subject is dead once it has errored: next() on it throws and subscribers get the
		// old error immediately. A previous failed attempt leaves it in that state on purpose, so
		// start each attempt with a fresh one.
		this.refreshTokenSubject = new BehaviorSubject<string>(null);

		this.http
			.post<UserLogin>(`${environment.apiUrl}authentication/refresh-access-token`, { userId, refreshToken })
			.pipe(
				timeout(REFRESH_TIMEOUT_MS),
				// One retry for transient failures only. A deploy drain can 502 a single request,
				// and without this every request queued behind it fails. A refusal is not
				// transient: retrying it just delays the logout.
				retry({
					count: 1,
					delay: (err) => (this.isTerminal(err) ? throwError(() => err) : timer(REFRESH_RETRY_DELAY_MS)),
				}),
				finalize(() => {
					this.isRefreshing = false;
				})
			)
			// Subscribed here rather than handed to the caller: a caller that unsubscribes on
			// navigation would otherwise cancel the refresh and strand everything queued behind it.
			.subscribe({
				next: (userLogin) => {
					// The refresh can succeed against a session this tab no longer owns: another
					// tab may have signed in as someone else, or started an impersonation, while
					// this one sat in the background. The store refuses the write in that case,
					// and without noticing, this tab would keep its expired token and 401 its way
					// through a refresh on every single request until the cap finally arrived.
					if (!this.userService.completeTokenRefresh(userLogin, refreshToken)) {
						console.warn(`${AUTH_LOG_PREFIX} stored session is no longer ours — reloading to adopt it`);
						this.refreshTokenSubject.error('Your session changed in another tab.');
						location.reload();
						return;
					}
					this.refreshTokenSubject.next(userLogin.token);
				},
				error: (err) => {
					datadogRum.addError(new Error('Token refresh failed'), {
						status: err?.status,
						statusText: err?.statusText,
						terminal: this.isTerminal(err),
					});

					if (this.isTerminal(err)) {
						console.error(`${AUTH_LOG_PREFIX} refresh refused — signing out`, { status: err?.status });
						// Erroring the subject releases every queued request instead of leaving
						// their spinners running forever. A normalised string keeps subscribers
						// from surfacing "[object Object]" to the user.
						this.refreshTokenSubject.error(this.utilityService.getErrorMessage(err));
						this.forceLogout();
					} else {
						console.warn(`${AUTH_LOG_PREFIX} refresh failed transiently — session kept`, {
							status: err?.status,
						});
						this.refreshTokenSubject.error(this.utilityService.getErrorMessage(err));
					}
				},
			});

		return this.refreshTokenSubject.pipe(
			filter((token) => token !== null),
			take(1)
		);
	}

	/**
	 * Refreshes first if the stored token is close to expiry, then completes. For callers that
	 * read the token directly instead of going through the interceptor, where a 401 has no
	 * recovery path.
	 */
	ensureFresh(): Observable<void> {
		const current = this.userService.currentUserValue;
		if (!current?.token) {
			return of(undefined);
		}

		if (this.remainingSeconds(current) > FRESHNESS_THRESHOLD_SECONDS) {
			return of(undefined);
		}

		return this.refresh().pipe(
			map(() => undefined),
			catchError(() => of(undefined))
		);
	}

	/** Promise form, for the fetch/jQuery call sites that are not observable-based. */
	ensureFreshAsync(): Promise<void> {
		return new Promise<void>((resolve) => {
			this.ensureFresh().subscribe({ next: () => resolve(), error: () => resolve() });
		});
	}

	/**
	 * A refusal is terminal: the session is over and no amount of retrying changes that. The
	 * server answers 401 both for an invalid refresh token and for a session that has reached its
	 * cap. 400 is treated the same way defensively, so a malformed-request answer cannot put the
	 * client into a retry loop against an endpoint that will never accept it.
	 */
	private isTerminal(err: { status?: number }): boolean {
		return err?.status === 400 || err?.status === 401 || err?.status === 403;
	}

	private remainingSeconds(current: UserLogin): number {
		// A session stored before receivedAt existed cannot be judged; treat it as due so it is
		// refreshed once rather than assumed good.
		if (!Number.isFinite(current.receivedAt)) {
			return 0;
		}

		const claims = readJwtClaims(current.token);
		const issuedAt = Number(claims?.iat);
		const expiresAt = Number(claims?.exp);
		const lifetime =
			Number.isFinite(issuedAt) && Number.isFinite(expiresAt) && expiresAt > issuedAt
				? expiresAt - issuedAt
				: FALLBACK_TOKEN_LIFETIME_SECONDS;

		const elapsed = (Date.now() - current.receivedAt) / 1000;
		return lifetime - elapsed;
	}

	/**
	 * The page the user was on, if it is one signing in again should return them to.
	 *
	 * The route is read from the hash, not from `location.pathname`. This app routes on the hash
	 * (`useHash: true`), which pins `pathname` at '/' on every page, so a pathname-based reading
	 * would look correct in a unit test and be unreachable in the browser.
	 *
	 * Only an in-app route is ever handed over. The login component navigates to whatever return
	 * route it is given, so anything that could name another origin is refused rather than
	 * round-tripped, and the authentication routes are excluded so a failure on the login screen
	 * cannot point back at itself.
	 */
	private returnRoute(): string | null {
		const hash = location.hash ?? '';
		const route = hash.startsWith('#') ? hash.slice(1) : '';

		const isInAppRoute = route.startsWith('/') && !route.startsWith('//');
		if (!isInAppRoute || route === '/' || route.startsWith('/authentication')) {
			return null;
		}

		return route;
	}

	private loginUrlFor(returnRoute: string | null): string {
		return returnRoute === null ? LOGIN_URL : `${LOGIN_URL}?returnUrl=${encodeURIComponent(returnRoute)}`;
	}

	/**
	 * Ends the session and returns the user to the login screen.
	 *
	 * Shared storage is only cleared when the token that failed is still the stored one. A tab
	 * that has been in the background can fail on a token a sibling tab already replaced; wiping
	 * storage then would log the good session out too.
	 */
	forceLogout() {
		if (this.loggingOut) {
			return;
		}
		this.loggingOut = true;
		this.isRefreshing = false;

		// Captured now, synchronously, not inside finish(): the best-effort logout call below
		// yields the event loop, and in that gap the failed requests' own error paths navigate the
		// dying app to the default route. Observed on portal-test: finish() ran ~500ms after the
		// refusal, by which time the hash already said /dashboard.
		const returnRoute = this.returnRoute();
		const failedToken = this.userService.currentUserValue?.refreshToken;
		const userId = this.userService.currentUserValue?.user?.id;

		const finish = () => {
			const storedToken = this.userService.readStoredRefreshToken();
			if (storedToken !== null && storedToken !== failedToken) {
				// Another tab has since signed in, so this tab's token is merely stale rather
				// than the session being over. Reload instead of clearing storage: that adopts
				// the newer session on the way back up. Clearing alone would leave this tab with
				// no identity and no way back, so every subsequent click would fail silently.
				console.warn(`${AUTH_LOG_PREFIX} a newer session exists — reloading to adopt it`);
				location.reload();
				return;
			}

			this.userService.clearSessionStorage();
			this.userService.clearCurrentUser();
			datadogRum.clearUser();
			// Written after the storage clears so they cannot take it back out. The URL below also
			// carries the route, but only as a courtesy for the address bar: the app is still
			// running until the reload commits and rewrites that query string, so this store is
			// what the login page actually trusts.
			if (returnRoute !== null) {
				sessionStorage.setItem(POST_LOGOUT_RETURN_URL_KEY, returnRoute);
			}
			// Moving only the hash is a same-document navigation: the address bar changes but the
			// torn-down application keeps running, and components still holding the cleared session
			// dereference it. The reload is what actually restarts the app at the login route.
			location.assign(this.loginUrlFor(returnRoute));
			location.reload();
		};

		if (!userId || !failedToken) {
			finish();
			return;
		}

		// Server-side logout is best-effort; the timeout guarantees the redirect still fires if
		// it hangs, so the user is never stranded on a dead page.
		this.userService
			.logoutApi(userId, failedToken)
			.pipe(
				timeout(LOGOUT_API_TIMEOUT_MS),
				catchError(() => of(null))
			)
			.subscribe({ next: finish, error: finish });
	}
}
